import { supabase } from '@/integrations/supabase/client'
import type {
  SpecialistWithAssignments,
  TransferRequest,
  TransferResponse,
  UnassignRequest,
  UnassignResponse,
  AuditLogEntry
} from '@/types/specialist-assignment'

export class SpecialistAssignmentService {
  /**
   * Get all specialist assignments (admin only)
   */
  static async getAllAssignments(): Promise<SpecialistWithAssignments[]> {
    try {
      const { data, error } = await supabase
        .rpc('get_specialist_assignments_admin' as any)

      if (error) {
        console.error('Error fetching specialist assignments:', error)
        throw new Error(error.message)
      }

      return (data || []) as SpecialistWithAssignments[]
    } catch (error) {
      console.error('Failed to fetch specialist assignments:', error)
      throw error
    }
  }

  /**
   * Transfer a caregiver from one specialist to another
   */
  static async transferCaregiver(request: TransferRequest): Promise<TransferResponse> {
    try {
      const { data, error } = await supabase
        .rpc('transfer_caregiver_to_specialist' as any, {
          p_caregiver_id: request.caregiver_id,
          p_old_specialist_id: request.old_specialist_id,
          p_new_specialist_id: request.new_specialist_id,
          p_reason: request.reason || null
        })

      if (error) {
        console.error('Error transferring caregiver:', error)
        return {
          success: false,
          error: error.message
        }
      }

      return data as TransferResponse
    } catch (error) {
      console.error('Failed to transfer caregiver:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }
    }
  }

  /**
   * Unassign a caregiver from a specialist
   */
  static async unassignCaregiver(request: UnassignRequest): Promise<UnassignResponse> {
    try {
      const { data, error } = await supabase
        .rpc('unassign_caregiver_from_specialist' as any, {
          p_caregiver_id: request.caregiver_id,
          p_specialist_id: request.specialist_id,
          p_reason: request.reason
        })

      if (error) {
        console.error('Error unassigning caregiver:', error)
        return {
          success: false,
          error: error.message
        }
      }

      return data as UnassignResponse
    } catch (error) {
      console.error('Failed to unassign caregiver:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }
    }
  }

  /**
   * Get assignment history for a caregiver
   */
  static async getCaregiverHistory(caregiverId: string): Promise<AuditLogEntry[]> {
    try {
      const { data, error } = await supabase
        .rpc('get_caregiver_assignment_history' as any, {
          p_caregiver_id: caregiverId
        })

      if (error) {
        console.error('Error fetching caregiver history:', error)
        throw new Error(error.message)
      }

      return (data || []) as AuditLogEntry[]
    } catch (error) {
      console.error('Failed to fetch caregiver history:', error)
      throw error
    }
  }
}
